import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';

// ── Public group branding (logo, name, classes) ──
const GroupContext = createContext(null);

export const GroupProvider = ({ children }) => {
  const [group, setGroup] = useState(null);
  useEffect(() => {
    api.get('/public/group').then(r => {
      setGroup(r.data);
      document.title = `${r.data.shortName || r.data.name} — Members`;
    }).catch(() => setGroup({ name: 'Royal College 2008 Batch', classes: [] }));
  }, []);
  return <GroupContext.Provider value={{ group, setGroup }}>{children}</GroupContext.Provider>;
};

export const useGroup = () => useContext(GroupContext);

// ── Member session ──
const MemberContext = createContext(null);

export const MemberProvider = ({ children }) => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('memberToken')) { setMember(null); return null; }
    try {
      const { data } = await api.get('/member/me');
      setMember(data.member);
      return data.member;
    } catch {
      localStorage.removeItem('memberToken');
      setMember(null);
      return null;
    }
  }, []);

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  const signIn = (token, m) => {
    localStorage.setItem('memberToken', token);
    setMember(m);
  };

  const logout = () => {
    localStorage.removeItem('memberToken');
    setMember(null);
  };

  return (
    <MemberContext.Provider value={{ member, setMember, loading, signIn, logout, refresh }}>
      {children}
    </MemberContext.Provider>
  );
};

export const useMember = () => useContext(MemberContext);

// ── Admin session ──
const AdminContext = createContext(null);

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) { setLoading(false); return; }
    api.get('/admin/me')
      .then(r => setAdmin(r.data.admin))
      .catch(() => localStorage.removeItem('adminToken'))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/admin/login', { email, password });
    localStorage.setItem('adminToken', data.token);
    setAdmin(data.admin);
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setAdmin(null);
  };

  return <AdminContext.Provider value={{ admin, setAdmin, loading, login, logout }}>{children}</AdminContext.Provider>;
};

export const useAdmin = () => useContext(AdminContext);
